import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { BalanceLogTable } from '@/components/features/balance-logs/BalanceLogTable';
import { BalanceLogFilters } from '@/components/features/balance-logs/BalanceLogFilters';
import { CommissionSummaryCard } from '@/components/features/balance-logs/CommissionSummaryCard';
import { SystemMovementTable } from '@/components/features/balance-logs/SystemMovementTable';
import { SystemMovementFilters } from '@/components/features/balance-logs/SystemMovementFilters';
import { ExportButton } from '@/components/shared/ExportButton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBalanceLogs, useCommissionSummary, useSystemMovements } from '@/lib/hooks';
import { exportBalanceLogsToCSV } from '@/utils/export';
import type {
  BalanceLogFilters as BalanceLogFiltersType,
  SystemMovementFilters as SystemMovementFiltersType,
} from '@/lib/types';
import { DataTablePagination } from '@/components/shared';

export const Route = createFileRoute('/_auth/_admin/admin/balance-logs/')({
  component: BalanceLogsComponent,
});

function BalanceLogsComponent() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Аудит балансов"
        description="История всех изменений балансов пользователей и кошельков"
      />

      <Tabs defaultValue="operations">
        <TabsList>
          <TabsTrigger value="operations">Журнал операций</TabsTrigger>
          <TabsTrigger value="system-movement">Движение в системе</TabsTrigger>
        </TabsList>

        <TabsContent value="operations" className="space-y-6">
          <OperationsTab />
        </TabsContent>

        <TabsContent value="system-movement" className="space-y-6">
          <SystemMovementTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OperationsTab() {
  const [filters, setFilters] = useState<BalanceLogFiltersType>({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const { data, isLoading } = useBalanceLogs({
    ...filters,
    page,
    per_page: perPage,
  });

  const { data: commissionData, isLoading: commissionLoading } = useCommissionSummary({
    from_date: filters.from_date,
    to_date: filters.to_date,
  });

  const handleExport = () => {
    if (data?.data) {
      exportBalanceLogsToCSV(data.data);
    }
  };

  return (
    <>
      <div className="flex justify-end">
        <ExportButton onExportCSV={handleExport} disabled={!data?.data} />
      </div>

      <CommissionSummaryCard data={commissionData?.data} isLoading={commissionLoading} />

      <BalanceLogFilters filters={filters} onFiltersChange={setFilters} />

      <BalanceLogTable data={data?.data || []} isLoading={isLoading} />

      {data?.meta && (
        <DataTablePagination
          meta={data.meta}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
    </>
  );
}

function SystemMovementTab() {
  const [filters, setFilters] = useState<SystemMovementFiltersType>({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const { data, isLoading } = useSystemMovements({
    ...filters,
    page,
    per_page: perPage,
  });

  return (
    <>
      <SystemMovementFilters filters={filters} onFiltersChange={setFilters} />

      <SystemMovementTable data={data?.data || []} isLoading={isLoading} />

      {data?.meta && (
        <DataTablePagination
          meta={data.meta}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
    </>
  );
}
